declare global {
  interface Window {
    dataLayer?: unknown[]
  }
}

export interface GTMEventData {
  event: string
  [key: string]: unknown
}

export const pushToDataLayer = (data: GTMEventData): void => {
  try {
    if (typeof window === 'undefined' || !window.dataLayer || !data?.event) {
      return
    }

    window.dataLayer.push(data)

    if (process.env.NODE_ENV === 'development') {
      console.log('GTM Event sent:', data)
    }
  } catch (error) {
    console.warn('GTM event failed:', error)
  }
}

const sendGTMEvent = (eventName: string, userId?: string, tenantName?: string, additionalData: Record<string, unknown> = {}): void => {
  pushToDataLayer({
    event: eventName,
    user_id: userId ?? 'unknown',
    // user_idだけを送るとGTMの予約語のuser_idとして扱われ、GAの探索機能から除外されるため、jizaipad_user_idも送る
    jizaipad_user_id: userId ?? 'unknown',
    tenant_name: tenantName ?? 'unknown',
    ...additionalData
  })
}

/**
 * カメラに紐づくイベントを送る。cameraId が空文字なら送らない。
 *
 * 呼び出し側は `cameraId ?? ''` でカメラ未確定の状態を空文字へ潰しているため、
 * そのまま送ると GA 上で '' が独立した camera_id として集計される。
 * かといって camera_id を省いて送ると、GTM の dataLayer が直前のカメラの値を
 * 保持したままそれを流用してしまい、このモジュールが断とうとしている
 * 「持ち越しによる誤集計」がそのまま再発する。よって「送らない」が正しい。
 */
const sendCameraEvent = (
  eventName: string,
  cameraId: string,
  userId?: string,
  tenantName?: string
): void => {
  if (cameraId === '') return

  sendGTMEvent(eventName, userId, tenantName, { camera_id: cameraId })
}

export const trackMovieTagAttach = (cameraId: string, userId?: string, tenantName?: string): void => {
  sendCameraEvent('movie_tag_attach', cameraId, userId, tenantName)
}

export const trackUserInvite = (userId?: string, tenantName?: string): void => {
  sendGTMEvent('user_invite', userId, tenantName)
}

export const trackUserEdit = (userId?: string, tenantName?: string): void => {
  sendGTMEvent('user_edit', userId, tenantName)
}

export const trackUserDelete = (userId?: string, tenantName?: string): void => {
  sendGTMEvent('user_delete', userId, tenantName)
}

export const trackDeviceEdit = (cameraId: string, userId?: string, tenantName?: string): void => {
  sendCameraEvent('device_edit', cameraId, userId, tenantName)
}

export const trackDeviceAreaMove = (cameraId: string, userId?: string, tenantName?: string): void => {
  sendCameraEvent('device_area_move', cameraId, userId, tenantName)
}

export const trackAreaAdd = (userId?: string, tenantName?: string): void => {
  sendGTMEvent('area_add', userId, tenantName)
}

export const trackAreaEdit = (userId?: string, tenantName?: string): void => {
  sendGTMEvent('area_edit', userId, tenantName)
}

export const trackTagCreate = (userId?: string, tenantName?: string): void => {
  sendGTMEvent('tag_create', userId, tenantName)
}

export const trackTagDelete = (userId?: string, tenantName?: string): void => {
  sendGTMEvent('tag_delete', userId, tenantName)
}

export const trackClipEdit = (cameraId: string, userId?: string, tenantName?: string): void => {
  sendCameraEvent('clip_edit', cameraId, userId, tenantName)
}

export const trackClipDelete = (cameraId: string, userId?: string, tenantName?: string): void => {
  sendCameraEvent('clip_delete', cameraId, userId, tenantName)
}

export const trackLogout = (userId?: string, tenantName?: string): void => {
  sendGTMEvent('logout', userId, tenantName)
}

export const trackRecordPlayOpen = (cameraId: string, userId?: string, tenantName?: string): void => {
  sendCameraEvent('record_play_open', cameraId, userId, tenantName)
}

export const trackClipCreate = (cameraId: string, userId?: string, tenantName?: string): void => {
  sendCameraEvent('clip_create', cameraId, userId, tenantName)
}

export const trackViewerOpen = (cameraId: string, userId?: string, tenantName?: string): void => {
  sendCameraEvent('viewer_open', cameraId, userId, tenantName)
}

// 旧 jizaipad-viewer 時代のイベント名を継承して長期 GA データと連続観測できるようにする。
// camera_id は必ず引数で受け取る — GTM の dataLayer は一度 push した値を保持するため、
// 渡さないと直前に見ていたカメラの値が黙って載る。
export const trackPanTiltUse = (cameraId: string, userId?: string, tenantName?: string): void => {
  sendCameraEvent('pan_tilt_use', cameraId, userId, tenantName)
}

export const trackZoomUse = (cameraId: string, userId?: string, tenantName?: string): void => {
  sendCameraEvent('zoom_use', cameraId, userId, tenantName)
}

export const trackSoftwareZoomUse = (cameraId: string, userId?: string, tenantName?: string): void => {
  sendCameraEvent('software_zoom_use', cameraId, userId, tenantName)
}

export const trackPresetMove = (cameraId: string, userId?: string, tenantName?: string): void => {
  sendCameraEvent('preset_move', cameraId, userId, tenantName)
}

export const trackPresetAdd = (cameraId: string, userId?: string, tenantName?: string): void => {
  sendCameraEvent('preset_add', cameraId, userId, tenantName)
}

export const trackPresetDelete = (cameraId: string, userId?: string, tenantName?: string): void => {
  sendCameraEvent('preset_delete', cameraId, userId, tenantName)
}

export const trackHomePositionSet = (cameraId: string, userId?: string, tenantName?: string): void => {
  sendCameraEvent('home_position_set', cameraId, userId, tenantName)
}

export const trackHomePositionMove = (cameraId: string, userId?: string, tenantName?: string): void => {
  sendCameraEvent('home_position_move', cameraId, userId, tenantName)
}
